/**
 * allocationAdminService — authFetch wrappers around /api/admin/ai/allocations/.
 *
 * WQ-230 (Phase 4.9 α4). Consumes the 6 endpoints shipped in WQ-227:
 *   POST   /grant
 *   POST   /{allocation_id}/revoke
 *   GET    ?tenant_id=...&status=...&capability_type=...
 *   GET    /{allocation_id}
 *   POST   /tier-preset/apply
 *   GET    /{allocation_id}/usage?period_start=...
 *
 * All endpoints require AI_ADMIN permission (operator-only).
 */

export interface Allocation {
  id: string;
  tenant_id: string;
  capability_type: string;
  capability_id: string | null;
  tier_preset: string | null;
  quota_units: number | null;
  quota_period: string | null;
  default_quota_units: number | null;
  default_quota_period: string | null;
  status: string;
  granted_by: string | null;
  granted_at: string | null;
  revoked_by: string | null;
  revoked_at: string | null;
  notes: string | null;
  expires_at: string | null;
  current_used: number;
}

export interface UsageAggregate {
  id: string;
  allocation_id: string;
  tenant_id: string;
  capability_type: string;
  capability_id: string | null;
  period_start_at: string;
  counter_value: number;
  event_metadata: Record<string, unknown>;
}

export interface GrantRequest {
  tenant_id: string;
  capability_type: string;
  capability_id?: string | null;
  tier_preset?: string;
  quota_units?: number | null;
  quota_period?: string | null;
  granted_by?: string;
  notes?: string;
  expires_at?: string | null;
}

export interface ApplyTierPresetRequest {
  tenant_id: string;
  tier_preset: string;
  granted_by: string;
  only_missing?: boolean;
}

export interface AllocationFilters {
  tenant_id?: string;
  status?: string;
  capability_type?: string;
}

type AuthFetch = (url: string, options?: RequestInit) => Promise<Response>;

const BASE = '/api/admin/ai/allocations';

export async function listAllocations(
  authFetch: AuthFetch,
  filters: AllocationFilters = {},
): Promise<Allocation[]> {
  const params = new URLSearchParams();
  if (filters.tenant_id) params.set('tenant_id', filters.tenant_id);
  if (filters.status) params.set('status', filters.status);
  if (filters.capability_type) params.set('capability_type', filters.capability_type);
  const qs = params.toString();
  const url = qs ? `${BASE}?${qs}` : BASE;
  const res = await authFetch(url);
  if (!res.ok) throw new Error(`listAllocations failed: ${res.status}`);
  return res.json();
}

export async function getAllocation(authFetch: AuthFetch, id: string): Promise<Allocation> {
  const res = await authFetch(`${BASE}/${id}`);
  if (!res.ok) throw new Error(`getAllocation failed: ${res.status}`);
  return res.json();
}

export async function grantAllocation(
  authFetch: AuthFetch,
  body: GrantRequest,
): Promise<Allocation> {
  const res = await authFetch(`${BASE}/grant`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`grantAllocation failed: ${res.status} ${detail}`);
  }
  return res.json();
}

export async function revokeAllocation(
  authFetch: AuthFetch,
  id: string,
  body: { revoked_by: string; notes?: string },
): Promise<Allocation> {
  const res = await authFetch(`${BASE}/${id}/revoke`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`revokeAllocation failed: ${res.status} ${detail}`);
  }
  return res.json();
}

export async function applyTierPreset(
  authFetch: AuthFetch,
  body: ApplyTierPresetRequest,
): Promise<Allocation[]> {
  const res = await authFetch(`${BASE}/tier-preset/apply`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`applyTierPreset failed: ${res.status} ${detail}`);
  }
  return res.json();
}

export async function getAllocationUsage(
  authFetch: AuthFetch,
  id: string,
  periodStart?: string,
): Promise<UsageAggregate> {
  const url = periodStart
    ? `${BASE}/${id}/usage?period_start=${encodeURIComponent(periodStart)}`
    : `${BASE}/${id}/usage`;
  const res = await authFetch(url);
  if (!res.ok) throw new Error(`getAllocationUsage failed: ${res.status}`);
  return res.json();
}
